/**
 * @file recruitmentProcess.component.tsx
 * @path apps/website/src/features/recruitment/components/recruitmentProcess.component.tsx
 * @description Pure presentational component for the timeline.
 */

import { cn } from "@sirbastians/utils";
import { useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RecruitmentStep } from "../recruitment.types";
import { useRecruitmentFacade } from "../hooks/useRecruitmentFacade.hook";

const ContentCard = ({ step }: { step: RecruitmentStep }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.currentTime = 0;
      video.play().catch(() => {});
      const handleVideoEnd = () => video.pause();
      video.addEventListener("ended", handleVideoEnd);
      return () => video.removeEventListener("ended", handleVideoEnd);
    }
  }, [step.media.src]);

  return (
    <motion.div
      key={step.id}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ duration: 0.4, ease: "circOut" }}
      className="w-full flex flex-col gap-6"
    >
      <div className="overflow-hidden border-2 border-border bg-muted/30 aspect-video w-full">
        {step.media.type === "video" ? (
          <video
            ref={videoRef}
            src={step.media.src}
            muted
            playsInline
            className="h-full w-full object-cover"
          />
        ) : (
          <img
            src={step.media.src}
            alt={step.title}
            className="h-full w-full object-cover"
          />
        )}
      </div>
      <div className="space-y-3">
        <h3 className="text-2xl font-montserrat font-bold text-foreground">
          {step.title}
        </h3>
        <p className="text-lg font-open-sans text-muted-foreground leading-relaxed">
          {step.description}
        </p>
      </div>
    </motion.div>
  );
};

interface RecruitmentProcessProps {
  facade: ReturnType<typeof useRecruitmentFacade>["process"];
}

export const RecruitmentProcess = ({ facade }: RecruitmentProcessProps) => {
  const { steps, activeStepId, activeStep, actions } = facade;

  return (
    <section id="recruitment-process" className="px-6 lg:px-10 py-24">
      <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-5">
        <div className="lg:col-span-3 min-h-[500px] flex items-center">
          <AnimatePresence mode="wait">
            {activeStep && <ContentCard step={activeStep} />}
          </AnimatePresence>
        </div>

        <div className="lg:col-span-2 space-y-10">
          <div className="space-y-2">
            <span className="text-accent font-montserrat font-bold tracking-[0.2em] text-xs uppercase">
              Join the Fleet
            </span>
            <h2 className="text-4xl font-montserrat font-bold tracking-tighter sm:text-5xl">
              Our Process
            </h2>
          </div>

          <div className="relative flex flex-col gap-2">
            <div className="absolute left-[27px] top-4 bottom-4 w-0.5 bg-border hidden sm:block" />
            {steps.map((step) => {
              const isActive = activeStepId === step.id;
              return (
                <button
                  key={step.id}
                  onClick={() => actions.setActiveStepId(step.id)}
                  className="group relative flex w-full items-center gap-6 p-4 text-left transition-all duration-300 outline-none"
                >
                  <div
                    className={cn(
                      "z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-300 font-mono text-xs font-bold",
                      isActive
                        ? "bg-accent border-accent text-white"
                        : "bg-background border-border text-muted-foreground group-hover:border-accent group-hover:text-accent",
                    )}
                  >
                    {step.id}
                  </div>
                  <span
                    className={cn(
                      "font-montserrat text-lg font-bold transition-colors duration-300",
                      isActive
                        ? "text-foreground"
                        : "text-muted-foreground group-hover:text-foreground",
                    )}
                  >
                    {step.title}
                  </span>
                  {isActive && (
                    <motion.div
                      layoutId="activePointer"
                      className="absolute left-0 w-1 h-2/3 bg-accent hidden lg:block"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
